import Dexie from 'dexie';
import type { LeitourDatabase } from '../database/db';
import { getOrCreateDeviceId } from './deviceId';
import { createSyncBatch, type SyncMutationDraftWithBase } from './batch';
import { hasRelatedSyncConflict } from './conflictRelations';
import { projectLocalSyncPayload } from './localReplica';
import { payloadVersionForSyncPayload } from './canonical';
import {
  SYNC_CANONICALIZATION,
  SYNC_PAYLOAD_VERSION_V2,
  SYNC_PROTOCOL_V1,
  SYNC_PROTOCOL_VERSION,
  type SyncEntityState,
  type SyncMetadata,
  type SyncMutationDraft,
  type SyncOperation,
  type SyncOutboxRecord
} from './types';

export const createInitialSyncMetadata = (userId: string, deviceId: string): SyncMetadata => ({
  id: 'sync',
  protocolVersion: SYNC_PROTOCOL_VERSION,
  canonicalization: SYNC_CANONICALIZATION,
  userId,
  deviceId,
  cursor: { lastChangeId: '0' },
  bootstrapState: 'not_started',
  remoteRollout: { status: 'not_checked' }
});

export const nextOutboxQueueOrder = async (db: LeitourDatabase): Promise<number> => {
  const records = await db.syncOutbox.toArray();
  return records.reduce((maximum, record) => Math.max(maximum, record.queueOrder ?? 0), 0) + 1;
};

export const compareOutboxQueueOrder = (left: SyncOutboxRecord, right: SyncOutboxRecord): number =>
  left.queueOrder - right.queueOrder
  || left.createdAt.localeCompare(right.createdAt)
  || left.batchId.localeCompare(right.batchId);

const baseFor = (
  operation: SyncOperation,
  state: SyncEntityState | undefined,
  metadata: SyncMetadata,
  predecessorRevision?: string
): SyncMutationDraftWithBase['baseRevision'] => {
  if (predecessorRevision) return { state: 'known', revision: predecessorRevision };
  if (state) return { state: 'known', revision: state.remoteRevision };
  if (metadata.bootstrapState === 'complete' && operation === 'create') return { state: 'known', revision: '0' };
  return { state: 'unresolved', reason: 'bootstrap_required' };
};

export const persistLocalSyncBatch = async (
  db: LeitourDatabase,
  userId: string | null,
  drafts: SyncMutationDraft[],
  createdAt = new Date().toISOString()
): Promise<SyncOutboxRecord | undefined> => {
  if (!userId || drafts.length === 0) return undefined;

  const projectedDeletedSessionIds = new Set(drafts
    .filter(({ entityType, operation }) => entityType === 'reading_session' && operation === 'delete')
    .map(({ entityId }) => entityId));
  const projectedDrafts = await Promise.all(drafts.map(async (draft) => ({
    ...draft,
    payload: await projectLocalSyncPayload(
      db,
      draft.entityType,
      draft.payload,
      projectedDeletedSessionIds
    )
  } as SyncMutationDraft)));

  const openConflicts = await db.syncConflicts.where('status').equals('open').toArray();
  if (hasRelatedSyncConflict(projectedDrafts, openConflicts)) {
    throw new Error('Resolva o conflito de sincronização deste item antes de fazer novas alterações.');
  }

  const deviceId = getOrCreateDeviceId();
  let metadata = await db.syncMetadata.get('sync');
  if (!metadata) {
    metadata = createInitialSyncMetadata(userId, deviceId);
    await db.syncMetadata.add(metadata);
  } else if (metadata.userId !== userId) {
    throw new Error('A metadata de sincronização não pertence ao banco local ativo.');
  } else if (metadata.deviceId !== deviceId) {
    metadata = { ...metadata, deviceId };
    await db.syncMetadata.put(metadata);
  }

  if (metadata.protocolVersion === SYNC_PROTOCOL_V1
    && projectedDrafts.some((draft) => payloadVersionForSyncPayload(draft.entityType, draft.payload) === SYNC_PAYLOAD_VERSION_V2)) {
    throw new Error('O Sync Protocol V1 não pode criar outbox para conteúdo EPUB. O upgrade para Protocol V2 precisa ser concluído primeiro.');
  }

  const draftsWithBase: SyncMutationDraftWithBase[] = [];
  for (const draft of projectedDrafts) {
    const state = await db.syncEntityStates.get([draft.entityType, draft.entityId.toLowerCase()]);
    const activePredecessors = await db.syncOutbox
      .filter(({ status, batch }) =>
        status !== 'superseded'
        && batch.mutations.some(({ entityType, entityId }) =>
          entityType === draft.entityType && entityId === draft.entityId.toLowerCase()))
      .toArray();
    const predecessors = activePredecessors
      .filter(({ status }) => status !== 'conflicted')
      .sort(compareOutboxQueueOrder);
    const predecessor = predecessors.at(-1)?.batch.mutations.find(({ entityType, entityId }) =>
      entityType === draft.entityType && entityId === draft.entityId.toLowerCase());
    const predecessorRevision = predecessor?.baseRevision.state === 'known'
      ? (BigInt(predecessor.baseRevision.revision) + 1n).toString()
      : undefined;
    const operation = metadata.bootstrapState === 'complete'
      && draft.operation === 'update'
      && !state
      && activePredecessors.length === 0
      ? 'create'
      : draft.operation;
    draftsWithBase.push({
      ...draft,
      operation,
      baseRevision: predecessor && !predecessorRevision
        ? { state: 'unresolved', reason: 'pending_predecessor' }
        : baseFor(operation, state, metadata, predecessorRevision)
    });
  }

  const batch = await Dexie.waitFor(createSyncBatch({
    deviceId,
    mutations: draftsWithBase,
    createdAt,
    protocolVersion: metadata.protocolVersion
  }));
  const hasUnresolvedBase = batch.mutations.some(({ baseRevision }) => baseRevision.state === 'unresolved');
  const record: SyncOutboxRecord = {
    batchId: batch.batchId,
    userId,
    queueOrder: await nextOutboxQueueOrder(db),
    status: hasUnresolvedBase ? 'awaiting_bootstrap' : 'pending',
    batch,
    attemptCount: 0,
    createdAt: batch.createdAt,
    nextAttemptAt: batch.createdAt
  };
  await db.syncOutbox.add(record);
  return record;
};
